<script setup lang="ts">
import { KButton } from "@kaleidoscope-solutions/ks-design-system"
import { computed, ref } from "vue"
import Dropzone from "@/components/Controls/DropZone.vue"
import FileUpload from "@/components/Controls/FileUpload.vue"
import KRadio from "@/components/Controls/KRadio.vue"
import FileIcon from "@/components/Setup/FileIcon.svg?component"

interface Option {
  value: string
  label: string
}

interface Props {
  title: string
  description: string
  options: Option[]
  selectedValue: string
  name: string
  idPrefix: string
  uploadValue?: string
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

interface Emits {
  (event: "update:selectedValue", value: string): void
  (event: "uploadError", message: string): void
  (event: "filesUploaded", files: File[]): void
}

const uploadedFiles = ref<File[]>([])

const uploadedFileName = computed(() => {
  switch (uploadedFiles.value.length) {
    case 0:
      return null
    case 1:
      return uploadedFiles.value[0].name
    default:
      return `${uploadedFiles.value.length} files selected`
  }
})

const showDropbox = computed(() => {
  if (props.uploadValue) {
    return props.selectedValue === props.uploadValue
  }
  return false
})

function updateValue(value: string) {
  emit("update:selectedValue", value)
}

function filesUploaded(files: File[]) {
  emit("filesUploaded", files)
}

function handleDropzoneFile(files: File[] | null) {
  if (files && files.length > 0) {
    console.warn("Dropped files:", files.map((f) => f.name))
    try {
      // Store all files locally for display
      uploadedFiles.value = files
      filesUploaded(files)
    } catch (error: any) {
      console.error("Error uploading file:", error)
      emit("uploadError", (error?.message) ?? String(error))
    }
  }
}

function handleBrowseFiles(files: File[]) {
  if (files && files.length > 0) {
    console.warn("ContentDropbox browsed files:", files.map((f) => f.name))
    try {
      uploadedFiles.value = [...files]
      filesUploaded(files)
    } catch (error: any) {
      console.error("Error uploading file:", error)
      emit("uploadError", (error?.message) ?? String(error))
    }
  }
}

function resetUpload() {
  uploadedFiles.value = []
  filesUploaded([])
}
</script>

<template>
  <div class="flex flex-col gap-4 rounded-lg bg-gray-100 p-4">
    <div class="flex flex-col">
      <h4 class="pb-4 text-lg font-bold">
        {{ title }}
      </h4>
      <p>{{ description }}</p>
      <hr class="my-4 border-gray-400">
      <div class="flex h-full flex-col items-start space-y-6 md:justify-between lg:min-h-0 lg:flex-row lg:items-stretch lg:gap-12">
        <div
          class="flex flex-col items-start space-y-2"
          role="radiogroup"
        >
          <KRadio
            v-for="option in options"
            :id="`${idPrefix}-${option.value}`"
            :key="option.value"
            :model-value="selectedValue"
            :value="option.value"
            :name="name"
            class="w-auto"
            @update:model-value="updateValue"
          >
            <span class="first-letter:uppercase"> {{ option.label }}</span>
          </KRadio>
        </div>

        <div
          v-if="showDropbox"
          class="flex min-h-0 w-full flex-1 items-center rounded-md border border-dashed border-gray-600 p-4 lg:w-[540px]"
        >
          <div
            v-if="uploadedFileName"
            class="flex flex-1 flex-row items-center justify-center gap-2 text-center"
          >
            <FileIcon
              alt="file icon"
              class="size-6 align-middle"
            />

            {{ uploadedFileName }}

            <span
              class="cursor-pointer font-bold text-violet-700"
              @click="resetUpload"
            >
              Remove
            </span>
          </div>
          <Dropzone
            v-else
            class="flex min-h-0 flex-1 justify-center"
            @file-changed="handleDropzoneFile"
          >
            <FileUpload
              accept=".zip,.yaml,.yml"
              :multiple="true"
              @change="handleBrowseFiles"
            >
              <template #default="{ buttonClick }">
                <div class="flex items-center justify-center gap-3">
                  <div>Drag and drop a YAML file or</div>
                  <KButton
                    variety="secondary"
                    color="violet"
                    @click.prevent="buttonClick()"
                  >
                    Browse
                  </KButton>
                </div>
              </template>
            </FileUpload>
          </Dropzone>
        </div>
      </div>
    </div>
  </div>
</template>
